import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Search, MapPin, Loader2, Copy, ExternalLink, Check } from "lucide-react";

L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

const Recenter = ({ c }) => { const m = useMap(); m.setView([c.lat, c.lng], 14); return null; };

const shareText = (p) => `Locker InPost ${p.id} — ${p.address}, ${p.city_line}${p.hours ? ` (orari: ${p.hours})` : ""}. Mappa: ${p.maps_url}`;

function PointRow({ p, selected, onSelect }) {
  const copy = () => { navigator.clipboard.writeText(shareText(p)); toast.success("Indicazioni copiate: incollale al cliente"); };
  return (
    <div className={`rounded-lg border p-3 text-sm ${selected ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`locker-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium truncate">{p.name} <span className="text-xs text-muted-foreground">· {p.distance} m</span></p>
          <p className="text-xs text-muted-foreground">{p.address}, {p.city_line}{p.description ? ` · ${p.description}` : ""}</p>
          {p.hours && <p className="text-xs text-muted-foreground">Orari: {p.hours}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          {onSelect && <Button size="sm" variant={selected ? "default" : "outline"} onClick={() => onSelect(p)} data-testid={`locker-select-${p.id}`}>{selected ? <Check className="w-4 h-4" /> : "Scegli"}</Button>}
          <Button size="sm" variant="ghost" onClick={copy} title="Copia indicazioni per il cliente" data-testid={`locker-copy-${p.id}`}><Copy className="w-4 h-4" /></Button>
          <a href={p.maps_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost" title="Apri in Google Maps"><ExternalLink className="w-4 h-4" /></Button></a>
        </div>
      </div>
    </div>
  );
}

// Mappa dei punti InPost vicini a un indirizzo (OpenStreetMap). onSelect opzionale: senza, mostra solo indicazioni.
export function LockerPicker({ initialQuery = "", value, onSelect, testid = "locker-picker" }) {
  const [q, setQ] = useState(initialQuery);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) { toast.error("Scrivi un indirizzo o una città"); return; }
    setBusy(true); setData(null);
    try {
      const { data: d } = await api.get("/shipping/lockers", { params: { q } });
      const points = (Array.isArray(d.points) ? d.points : []).filter((point) => point?.type === "locker");
      setData({ ...d, points });
      if (value && !points.some((point) => point.id === value.id)) onSelect?.(null);
      if (!points.length) toast.info(d.points?.length ? "I risultati ricevuti non includono locker verificati. I punti di ritiro sono esclusi da questa mappa." : "Nessun locker InPost restituito per questa ricerca.");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ricerca non riuscita"); }
    finally { setBusy(false); }
  };
  const mapped = (data?.points || []).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng) && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180);
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Indirizzo, CAP o città" data-testid={`${testid}-query`} />
        <Button type="button" onClick={search} disabled={busy} className="gap-2" data-testid={`${testid}-search`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Cerca</Button>
      </div>
      {data && data.points.length > 0 && (
        <>
          {mapped.length > 0 && <div className="h-72 rounded-xl overflow-hidden border border-border" data-testid={`${testid}-map`}>
            <MapContainer center={[data.center.lat, data.center.lng]} zoom={14} style={{ height: "100%", width: "100%" }}>
              <Recenter c={data.center} />
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {mapped.map((p) => (
                <Marker key={p.id} position={[p.lat, p.lng]}>
                  <Popup><strong>{p.id}</strong><br />{p.address}<br />{p.hours}{onSelect && <><br /><button className="app-text-button text-blue-600 underline" data-testid={`${testid}-choose-${p.id}`} onClick={() => onSelect(p)}>Scegli questo punto</button></>}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>}
          {!mapped.length && <p className="text-xs text-muted-foreground" data-testid={`${testid}-map-unavailable`}>Coordinate non disponibili: consulta l’elenco dei locker verificati.</p>}
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {data.points.slice(0, 12).map((p) => <PointRow key={p.id} p={p} selected={value?.id === p.id} onSelect={onSelect} />)}
          </div>
        </>
      )}
      {value?.type === "locker" && <p className="text-sm flex items-center gap-1.5 text-teal-500" data-testid={`${testid}-chosen`}><MapPin className="w-4 h-4" /> Scelto: {value.id} — {value.address}, {value.city_line}</p>}
    </div>
  );
}
